import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AuthLayout } from "./auth-layout";
import { MailCheck } from "lucide-react";
import {
  CompactCard,
  CompactField,
  CompactFormActionButton,
  CompactFormFooter,
  CompactFormHeader,
} from "@/components/compact-ui";
import "@/components/compact-ui/compact-form.css";
import { isFormSaveDisabled } from "@/lib/form-save-disabled";

const forgotSchema = z.object({
  email: z
    .string()
    .min(1, "El email es requerido")
    .email("El email no es válido"),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

export function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: { email: "" },
  });

  const emailValue = watch("email");

  const saveDisabled = isFormSaveDisabled({
    canSave: !loading,
    hasRequiredValues: Boolean(emailValue?.trim()),
    hasRequiredFieldErrors: Boolean(errors.email),
  });

  const onSubmit = async (data: ForgotFormData) => {
    setError(null);
    try {
      setLoading(true);
      await authApi.forgotPassword(data.email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { message?: string }).message ||
        "Error al enviar la solicitud";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout>
        <div className="compact-form">
          <CompactFormHeader badge="Correo enviado" />
          <div className="compact-form-body">
            <CompactCard title="Revisa tu correo">
              <div className="compact-center grid gap-3 py-4 text-center">
                <div className="mx-auto rounded-full bg-primary/10 p-4">
                  <MailCheck className="h-8 w-8 text-primary" />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Si el email existe en nuestra base de datos, recibirás
                  instrucciones para restablecer tu contraseña en unos minutos.
                  Revisa también tu carpeta de spam.
                </p>
              </div>
            </CompactCard>
          </div>
          <CompactFormFooter>
            <CompactFormActionButton
              variant="secondary"
              label="Volver al login"
              onClick={() => navigate("/login")}
            />
          </CompactFormFooter>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="compact-form">
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <CompactFormHeader badge="Recuperar contraseña" />

          <div className="compact-form-body">
            <CompactCard
              title="Recuperar contraseña"
              hint="Te enviaremos un enlace para restablecer tu contraseña."
            >
              <CompactField
                label="Correo"
                htmlFor="forgot-email"
                required
                error={errors.email?.message}
              >
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="tu@email.com"
                  disabled={loading}
                  autoComplete="email"
                  {...register("email")}
                />
              </CompactField>

              {error ? (
                <p
                  className="compact-span-all text-[11px] text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
            </CompactCard>
          </div>

          <CompactFormFooter
            cancel={{
              label: "Volver al login",
              onClick: () => navigate("/login"),
              disabled: loading,
            }}
            submit={{
              label: "Enviar instrucciones",
              loadingLabel: "Enviando…",
              disabled: saveDisabled,
              isSubmitting: loading,
            }}
          />
        </form>
      </div>
    </AuthLayout>
  );
}
